import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatDate,
  formatMarketCap,
  formatMarketCapShort,
  formatMonthTick,
  formatPrice,
  formatShares,
  formatSharesCompact,
} from '../lib/format';

// CVP Ledger palette. Signature red carries the market cap — the brand's rule is
// that red marks what matters, and the cap line is the point of the page. The
// shares panel takes the brand's "active" blue; the pair validates as categorical
// on the white card surface (CVD ΔE 20.3, normal-vision ΔE 29.9, both over 3:1).
const CAP_COLOR = '#bb2027';
const SHARES_COLOR = '#1e5aa8';
const GRID = '#e0e2df';
const AXIS = '#c9ccc7';
const MUTED = '#7a8089';
const INK_SOFT = '#4a4f57';
const SURFACE = '#ffffff';
const TICK_FONT = "'IBM Plex Mono', ui-monospace, monospace";

/**
 * How far above the threshold the series may sit before drawing the line stops being
 * worth the compression. Forcing $5M into the domain for a $3T company would squash
 * every real movement into a flat line to show a rule the company is nowhere near.
 */
const THRESHOLD_VISIBLE_WITHIN = 10;

/**
 * How far above the axis floor a reference level must sit to be worth drawing, as a
 * fraction of the plot's height. Being inside the domain is not enough: a trillion-dollar
 * chart's floor rounds to zero, which technically "includes" $10M and $20M, and drawing
 * them there puts two labelled rules on the baseline of a company a hundred thousand
 * times above them — implying a proximity that isn't there.
 */
const LEVEL_MIN_HEADROOM = 0.02;

/** ~7 evenly spaced dates from the series, so ticks never collide. */
function pickTicks(series, count = 7) {
  if (series.length <= count) return series.map((p) => p.date);
  const step = (series.length - 1) / (count - 1);
  return Array.from({ length: count }, (_, i) => series[Math.round(i * step)].date);
}

/**
 * A domain and tick set on round numbers.
 *
 * Recharts' automatic ticks land wherever the padded domain happens to fall, which
 * gives axes labelled $712.11B / $2.21T / $3.71T. Snapping the step to 1/2/2.5/5 ×
 * 10ⁿ and the bounds to a multiple of it produces ticks a reader can actually use.
 * The domain is not forced to zero: a series that only moves between $2.5T and $3.5T
 * would be flattened into a straight line by a zero baseline.
 */
function niceScale(values, intervals = 5) {
  if (!values.length) return { domain: ['auto', 'auto'], ticks: undefined };
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (!(max > min)) return { domain: ['auto', 'auto'], ticks: undefined };

  const rough = (max - min) / intervals;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const step = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10) * magnitude;

  const low = Math.floor(min / step) * step;
  const high = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = low; v <= high + step / 2; v += step) ticks.push(Number(v.toPrecision(12)));
  return { domain: [low, high], ticks };
}

function ChartTooltip({ active, label, byDate }) {
  if (!active || !label) return null;
  const point = byDate.get(label);
  if (!point) return null;

  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-date">{formatDate(point.date)}</div>
      <div className="chart-tooltip-row">
        <span>Market cap</span>
        <strong>{formatMarketCap(point.marketCap)}</strong>
      </div>
      <div className="chart-tooltip-row">
        <span>Close</span>
        <strong>{formatPrice(point.close)}</strong>
      </div>
      <div className="chart-tooltip-row">
        <span>Shares</span>
        <strong>{formatShares(point.shares)}</strong>
      </div>
      <div className="chart-tooltip-foot">
        Share count from the {point.form} as of {formatDate(point.asOfDate)}
      </div>
    </div>
  );
}

/**
 * Market cap over time, with shares outstanding as an optional second panel.
 *
 * The two measures live in separate stacked panels rather than sharing one plot on
 * two y-scales: a dual axis lets the reader "see" crossings and divergences that are
 * pure artefacts of where the two scales happen to be pinned. Stacked panels share
 * the x-axis and a synchronised crosshair, so the same comparison is available and
 * every vertical position still means one thing.
 */
export default function MarketCapChart({
  series,
  observations,
  showShares,
  showFilingMarks,
  threshold,
  referenceLevels,
}) {
  const byDate = useMemo(() => new Map(series.map((p) => [p.date, p])), [series]);
  const ticks = useMemo(() => pickTicks(series), [series]);
  const sharesScale = useMemo(() => niceScale(series.map((p) => p.shares), 2), [series]);

  // The threshold is pulled into the y-domain so the line is always on screen when it
  // is relevant — otherwise a series that never approaches it would leave the rule
  // clipped off the bottom, which reads as "no line" rather than "well clear of it".
  const { capScale, showThreshold, levelsInScale } = useMemo(() => {
    const caps = series.map((p) => p.marketCap);
    const visible = Boolean(threshold) && caps.length > 0 && Math.min(...caps) <= threshold * THRESHOLD_VISIBLE_WITHIN;
    const scale = niceScale(visible ? [...caps, threshold] : caps, 5);

    // Drawn only where the axis already goes. The domain is settled by the data and the
    // threshold above; these levels read it rather than change it, so a chart whose top
    // is $7M simply doesn't show the $10M and $20M rules.
    const [low, high] = scale.domain;
    const floor = typeof low === 'number' ? low + (high - low) * LEVEL_MIN_HEADROOM : null;
    const levels =
      floor === null
        ? []
        : (referenceLevels ?? []).filter((v) => v >= floor && v <= high && v !== threshold);

    return { capScale: scale, showThreshold: visible, levelsInScale: levels };
  }, [series, threshold, referenceLevels]);

  // A filing's as-of date can land on a weekend or holiday, where there is no bar to
  // anchor to. Snap each marker to the first trading day on or after it.
  const filingMarks = useMemo(() => {
    if (!showFilingMarks) return [];
    return observations
      .map((obs) => series.find((p) => p.date >= obs.asOfDate)?.date)
      .filter((date, i, all) => date && all.indexOf(date) === i);
  }, [observations, series, showFilingMarks]);

  if (!series.length) {
    return <div className="empty">No overlapping price and filing data to chart.</div>;
  }

  const sharedAxis = {
    dataKey: 'date',
    ticks,
    tickFormatter: formatMonthTick,
    tick: { fill: MUTED, fontSize: 10, fontFamily: TICK_FONT, letterSpacing: '0.06em' },
    tickLine: false,
    axisLine: { stroke: AXIS },
    minTickGap: 8,
  };

  return (
    <>
      <ResponsiveContainer width="100%" height={showShares ? 300 : 360}>
        <LineChart data={series} syncId="mktcap" margin={{ top: 8, right: 30, bottom: 8, left: 8 }}>
          <CartesianGrid stroke={GRID} strokeWidth={1} vertical={false} />
          <XAxis {...sharedAxis} hide={showShares} />
          <YAxis
            domain={capScale.domain}
            ticks={capScale.ticks}
            tickFormatter={formatMarketCap}
            tick={{ fill: MUTED, fontSize: 10, fontFamily: TICK_FONT }}
            tickLine={false}
            axisLine={false}
            interval={0}
            width={64}
          />
          {filingMarks.map((date) => (
            <ReferenceLine key={date} x={date} stroke={GRID} strokeWidth={1} />
          ))}
          {/* Lighter than the threshold rule and drawn under it: these are orientation,
              and the $5M line is the one the page is actually about. */}
          {levelsInScale.map((level) => (
            <ReferenceLine
              key={level}
              y={level}
              stroke={MUTED}
              strokeWidth={1}
              strokeDasharray="2 5"
              label={{
                value: formatMarketCapShort(level),
                position: 'insideTopRight',
                fill: MUTED,
                fontSize: 10,
                fontFamily: TICK_FONT,
                letterSpacing: '0.04em',
              }}
            />
          ))}
          {/* The threshold rule. Dashed and in ink, not red: red belongs to the cap line
              here, and a second red mark would read as part of the data. */}
          {showThreshold && (
            <ReferenceLine
              y={threshold}
              stroke={INK_SOFT}
              strokeWidth={1}
              strokeDasharray="5 4"
              label={{
                value: formatMarketCapShort(threshold),
                position: 'insideTopRight',
                fill: INK_SOFT,
                fontSize: 10,
                fontFamily: TICK_FONT,
                letterSpacing: '0.04em',
              }}
            />
          )}
          <Tooltip
            content={<ChartTooltip byDate={byDate} />}
            cursor={{ stroke: AXIS, strokeWidth: 1 }}
          />
          <Line
            type="monotone"
            dataKey="marketCap"
            stroke={CAP_COLOR}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={false}
            activeDot={{ r: 4, fill: CAP_COLOR, stroke: SURFACE, strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>

      {showShares && (
        <>
          <p className="panel-label">
            {series[0]?.adsRatio
              ? `ADS equivalents — ordinary shares ÷ ${series[0].adsRatio}, split-adjusted`
              : 'Shares outstanding — traded class, split-adjusted'}
          </p>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={series} syncId="mktcap" margin={{ top: 8, right: 30, bottom: 8, left: 8 }}>
              <CartesianGrid stroke={GRID} strokeWidth={1} vertical={false} />
              <XAxis {...sharedAxis} />
              <YAxis
                domain={sharesScale.domain}
                ticks={sharesScale.ticks}
                tickFormatter={formatSharesCompact}
                tick={{ fill: MUTED, fontSize: 10, fontFamily: TICK_FONT }}
                tickLine={false}
                axisLine={false}
                interval={0}
                width={64}
              />
              {filingMarks.map((date) => (
                <ReferenceLine key={date} x={date} stroke={GRID} strokeWidth={1} />
              ))}
              <Tooltip
                content={<ChartTooltip byDate={byDate} />}
                cursor={{ stroke: AXIS, strokeWidth: 1 }}
              />
              {/* stepAfter, not a smooth curve: the count is flat between filings and
                  changes on one known day. A curve would imply daily readings. */}
              <Line
                type="stepAfter"
                dataKey="shares"
                stroke={SHARES_COLOR}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 4, fill: SHARES_COLOR, stroke: SURFACE, strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </>
  );
}
